// -----------------------------------------------------------------------------
// Pack chips: badges SAVE usan solo variant.price / compare_at.
// La etiqueta .nad-pack-chip__per ($/Can) usa precio del selling plan seleccionado cuando aplica.
// -----------------------------------------------------------------------------

type NadSellingPlanAllocationJson = {
  sellingPlanId: number;
  price: number;
  compareAtPrice: number | null;
};

type NadPackVariantJson = {
  id: number;
  title?: string;
  price: number;
  compareAtPrice: number | null;
  packSize: number;
  packLabel: string;
  sellingPlanAllocations?: NadSellingPlanAllocationJson[];
};

type NadPackDataJson = {
  currency: string;
  moneyFormat: string;
  variants: NadPackVariantJson[];
};

function parsePackSizeFromLabel(text: string): number | null {
  const m = text.match(/(\d+)\s*[-]?\s*pack/i);
  return m?.[1] ? parseInt(m[1], 10) : null;
}

function findPackSizeOptionSetContainer(): Element | null {
  return (
    document.querySelector(
      '.mpg-swatch [os-name="Pack Size"].mpg-swatch__option-set'
    ) ||
    document.querySelector(
      '.mpg-swatch [os-name="Pack size"].mpg-swatch__option-set'
    ) ||
    document.querySelector('[os-name="Pack Size"].mpg-swatch__option-set') ||
    document.querySelector('[os-name="Pack size"].mpg-swatch__option-set')
  );
}

/**
 * Chips de pack: la app suele usar `.mpg-swatch__option`, pero a veces solo enlaces con `variant=`.
 */
function queryPackSizeChipElements(container: Element): HTMLElement[] {
  const fromClass = Array.from(
    container.querySelectorAll<HTMLElement>(".mpg-swatch__option")
  );
  if (fromClass.length) return fromClass;

  const label = container.querySelector(".mpg-swatch__label");
  const chips = new Map<HTMLElement, true>();

  container
    .querySelectorAll<HTMLElement>('a[href*="variant="]')
    .forEach((a) => {
      if (label?.contains(a)) return;
      const root = a.closest<HTMLElement>(".mpg-swatch__option") || a;
      chips.set(root, true);
    });

  if (!chips.size) {
    container.querySelectorAll<HTMLElement>("[data-variant-id]").forEach((node) => {
      if (label?.contains(node)) return;
      const root =
        node.closest<HTMLElement>(".mpg-swatch__option") ||
        node.closest<HTMLElement>("button") ||
        node.closest<HTMLElement>("a") ||
        node;
      if (container.contains(root)) chips.set(root, true);
    });
  }

  return Array.from(chips.keys());
}

function readNadPackDataJson(): NadPackDataJson | null {
  const packSet = document.querySelector(
    '[os-name="Pack Size"].mpg-swatch__option-set, [os-name="Pack size"].mpg-swatch__option-set'
  );
  const fromPackTree =
    packSet?.closest("product-info")?.querySelector<HTMLScriptElement>(
      'script[id^="NadProductPackData-"]'
    );
  const script =
    fromPackTree ||
    document.querySelector<HTMLScriptElement>(".template-product script[id^='NadProductPackData-']") ||
    document.querySelector<HTMLScriptElement>("script[id^='NadProductPackData-']");
  const raw = script?.textContent?.trim();
  if (!raw) return null;
  try {
    return JSON.parse(raw) as NadPackDataJson;
  } catch {
    return null;
  }
}

const NAD_PACK_CUBE_PATH_D =
  "M12.1664 4.62729C12.0407 4.57702 11.9005 4.57702 11.7748 4.62729L5.48194 7.14443L8.01648 8.15825L14.5051 5.56279L12.1664 4.62729ZM15.9247 6.13061L9.43604 8.72607L11.9706 9.73989L18.4592 7.14443L15.9247 6.13061ZM19.3516 7.92314L12.4978 10.6647V19.0189L19.3516 16.2773V7.92314ZM11.4434 19.0189V10.6647L4.58958 7.92314V16.2773L11.4434 19.0189ZM11.3832 3.64828C11.7603 3.49745 12.1809 3.49745 12.558 3.64828L20.0746 6.65492C20.2748 6.73499 20.406 6.92885 20.406 7.14443V16.2773C20.406 16.7085 20.1435 17.0962 19.7432 17.2564L12.1664 20.2871C12.0407 20.3374 11.9005 20.3374 11.7748 20.2871L4.19798 17.2564C3.79766 17.0962 3.53516 16.7085 3.53516 16.2773V7.14443C3.53516 6.92885 3.66641 6.73499 3.86657 6.65492L11.3832 3.64828Z";

function createPackCubeIcon(): SVGSVGElement {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  svg.setAttribute("width", "24");
  svg.setAttribute("height", "24");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("fill", "none");
  svg.setAttribute("aria-hidden", "true");
  svg.classList.add("nad-pack-chip__cube");
  for (const fill of ["#193547", "#3475DC"] as const) {
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute("d", NAD_PACK_CUBE_PATH_D);
    path.setAttribute("fill", fill);
    svg.appendChild(path);
  }
  return svg;
}

function stripPackChipEnrichmentText(el: HTMLElement): string {
  const clone = el.cloneNode(true) as HTMLElement;
  clone
    .querySelectorAll(
      ".nad-pack-chip__icons, .nad-pack-chip__per-wrap, .nad-pack-chip__per, .nad-pack-chip__save"
    )
    .forEach((n) => n.remove());
  return clone.textContent?.replace(/\s+/g, " ").trim() || "";
}

function getVariantIdFromPackChipEl(el: HTMLElement): string | null {
  const fromAttr = (node: Element | null) => {
    if (!node) return null;
    return (
      node.getAttribute("data-variant-id") ||
      node.getAttribute("data-variantid") ||
      null
    );
  };

  const self = fromAttr(el);
  if (self) return self;

  const descendant = el.querySelector("[data-variant-id],[data-variantid]");
  const fromDesc = fromAttr(descendant);
  if (fromDesc) return fromDesc;

  const anchor = el.querySelector<HTMLAnchorElement>('a[href*="variant="]');
  if (anchor?.href) {
    const m = anchor.href.match(/variant=(\d+)/);
    return m?.[1] ?? null;
  }

  const input = el.querySelector<HTMLInputElement>(
    'input[name="id"], input[data-variant-id]'
  );
  const fromInput = input?.value || fromAttr(input);
  if (fromInput) return fromInput;

  return null;
}

function packSizeForVariantRow(
  row: NadPackVariantJson,
  chipPlainText: string
): number {
  if (row.packSize > 0) return row.packSize;
  const fromTitle = row.title ? parsePackSizeFromLabel(row.title) : null;
  return (
    parsePackSizeFromLabel(chipPlainText) ||
    parsePackSizeFromLabel(row.packLabel) ||
    fromTitle ||
    0
  );
}

function sellingPlanIdsMatch(formValue: string, allocId: number): boolean {
  if (String(allocId) === formValue) return true;
  const m = formValue.match(/SellingPlan\/(\d+)/);
  if (m?.[1] === String(allocId)) return true;
  return false;
}

function findSellingPlanAllocation(
  row: NadPackVariantJson,
  sellingPlanId: string
): NadSellingPlanAllocationJson | undefined {
  if (!row.sellingPlanAllocations?.length) return undefined;
  return row.sellingPlanAllocations.find(
    (a) =>
      String(a.sellingPlanId) === sellingPlanId ||
      sellingPlanIdsMatch(sellingPlanId, a.sellingPlanId)
  );
}

/** Precio efectivo para una fila del JSON: allocation del plan seleccionado o compra única. */
function effectiveVariantPrices(
  row: NadPackVariantJson,
  sellingPlanId: string | null
): { price: number; compareAtPrice: number | null } {
  if (sellingPlanId) {
    const alloc = findSellingPlanAllocation(row, sellingPlanId);
    if (alloc && alloc.price > 0) {
      return { price: alloc.price, compareAtPrice: alloc.compareAtPrice };
    }
  }
  return { price: row.price, compareAtPrice: row.compareAtPrice };
}

/** selling_plan activo en el DOM (Subscribe & Save / Recharge); null = one-time. */
function getSelectedSellingPlanIdFromDom(): string | null {
  const roots: Element[] = [];
  const pi = document.querySelector("product-info");
  const tp = document.querySelector(".template-product");
  if (pi) roots.push(pi);
  if (tp) roots.push(tp);
  if (!roots.length) roots.push(document.body);

  for (const root of roots) {
    const selects = root.querySelectorAll<HTMLSelectElement>(
      'select[name="selling_plan"]'
    );
    for (const sel of selects) {
      if (sel.value) return sel.value;
    }

    const radios = root.querySelectorAll<HTMLInputElement>(
      'input[type="radio"][name="selling_plan"]'
    );
    for (const r of radios) {
      if (r.checked && r.value) return r.value;
    }

    const hiddenOrText = root.querySelectorAll<HTMLInputElement>(
      'input[name="selling_plan"]:not([type="radio"])'
    );
    for (const inp of hiddenOrText) {
      if (inp.value) return inp.value;
    }
  }

  return null;
}

/**
 * $/lata mostrado en .nad-pack-chip__per: sigue selling plan seleccionado (precio allocation).
 * Jerarquía de “compare” para tachados en otros contextos: compare allocation, one-time vs sub, compare_at variant.
 */
function perCanForChipDisplay(
  row: NadPackVariantJson,
  chipPlainText: string,
  sellingPlanId: string | null
): { salePerCan: number; comparePerCan: number | null } | null {
  const pack = packSizeForVariantRow(row, chipPlainText);
  if (pack <= 0) return null;

  const eff = effectiveVariantPrices(row, sellingPlanId);
  if (eff.price <= 0) return null;

  const salePerCan = Math.round(eff.price / pack);
  let comparePerCan: number | null = null;

  if (eff.compareAtPrice != null && eff.compareAtPrice > eff.price) {
    const c = Math.round(eff.compareAtPrice / pack);
    if (c > salePerCan) comparePerCan = c;
  }

  if (comparePerCan == null && sellingPlanId && row.price > eff.price) {
    const oneTime = Math.round(row.price / pack);
    if (oneTime > salePerCan) comparePerCan = oneTime;
  }

  if (
    comparePerCan == null &&
    row.compareAtPrice != null &&
    row.compareAtPrice > row.price
  ) {
    const c = Math.round(row.compareAtPrice / pack);
    if (c > salePerCan) comparePerCan = c;
  }

  return { salePerCan, comparePerCan };
}

/** Ahorro total del pack (compare_at − precio venta variant), centavos. */
function packLineSaveCents(
  row: NadPackVariantJson,
  chipPlainText: string
): number | null {
  const pack = packSizeForVariantRow(row, chipPlainText);
  if (pack <= 0 || row.price <= 0) return null;

  if (row.compareAtPrice == null || row.compareAtPrice <= row.price) return null;
  return row.compareAtPrice - row.price;
}

/** Evita bucle: nuestro enrich modifica el DOM y el MutationObserver volvería a disparar schedule(). */
function isNodeFromPackChipInjection(node: Node): boolean {
  if (node.nodeType === Node.TEXT_NODE) {
    const p = node.parentElement;
    return p != null && p.closest("[class*='nad-pack-chip__']") != null;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return false;
  return (node as Element).closest("[class*='nad-pack-chip__']") != null;
}

function isChildListOnlyPackChipSelfMutation(record: MutationRecord): boolean {
  if (record.type !== "childList") return false;
  for (const n of Array.from(record.addedNodes)) {
    if (!isNodeFromPackChipInjection(n)) return false;
  }
  for (const n of Array.from(record.removedNodes)) {
    if (!isNodeFromPackChipInjection(n)) return false;
  }
  return true;
}

/** true = conviene volver a ejecutar enrich (MPG, selling_plan, etc.). */
function shouldSchedulePackChipEnrichmentFromMutations(
  records: MutationRecord[]
): boolean {
  for (const r of records) {
    if (r.type === "attributes") return true;
    if (r.type === "childList" && !isChildListOnlyPackChipSelfMutation(r)) return true;
  }
  return false;
}

const RECHARGE_WIDGET_CSS_LINK = "data-nad-recharge-widget-css";

const rechargeShadowRootsWithObservers = new WeakSet<ShadowRoot>();

/** Every ShadowRoot under a custom element (host + nested web components). */
function collectDescendantShadowRoots(host: Element): ShadowRoot[] {
  const roots: ShadowRoot[] = [];
  const stack: Element[] = [host];
  while (stack.length) {
    const el = stack.pop()!;
    if (el.shadowRoot) {
      roots.push(el.shadowRoot);
      el.shadowRoot.querySelectorAll("*").forEach((node) => {
        if (node instanceof Element) stack.push(node);
      });
    }
  }
  return roots;
}

function injectRechargeMinCssIntoShadowRoots(
  widget: Element,
  href: string
): void {
  if (!href) return;
  for (const sr of collectDescendantShadowRoots(widget)) {
    if (!rechargeShadowRootsWithObservers.has(sr)) {
      rechargeShadowRootsWithObservers.add(sr);
      new MutationObserver(() => {
        injectRechargeMinCssIntoShadowRoots(widget, href);
      }).observe(sr, { childList: true, subtree: true });
    }
    if (sr.querySelector(`link[${RECHARGE_WIDGET_CSS_LINK}]`)) continue;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    link.setAttribute(RECHARGE_WIDGET_CSS_LINK, "1");
    sr.insertBefore(link, sr.firstChild);
  }
}

function formatPerCanLabel(perCanCents: number, currency: string): string {
  const w = window as any;
  if (w.Shopify && typeof w.Shopify.formatMoney === "function") {
    const moneyFormat =
      w.Shopify.money_format || w.Shopify.moneyFormat || "{{amount}}";
    return `${w.Shopify.formatMoney(perCanCents, moneyFormat)}/Can`;
  }
  try {
    return `${new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
    }).format(perCanCents / 100)}/Can`;
  } catch {
    return `$${(perCanCents / 100).toFixed(2)}/Can`;
  }
}

/** Monto total del pack para badge "SAVE …" (sin sufijo /Can). */
function formatSaveBadgeAmount(cents: number, currency: string): string {
  const w = window as any;
  if (w.Shopify && typeof w.Shopify.formatMoney === "function") {
    const moneyFormat =
      w.Shopify.money_format || w.Shopify.moneyFormat || "{{amount}}";
    return w.Shopify.formatMoney(cents, moneyFormat);
  }
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
    }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

class NADProductTemplate {
  private $product: HTMLElement | null;
  private productFormParent: HTMLElement | null = null;
  private rechargeWidget: HTMLElement | null = null;
  private rechargeShadowObserver: MutationObserver | null = null;

  constructor() {
    this.$product = document.querySelector(".template-product");
    if (this.$product) {
      this.init();
    }
  }

  init() {
    // Add product page-specific initialization logic here
    this.removeGrouptifyLinks();
    this.initMobileSwatchSlider();
    this.initBuyButtons();
    this.initOkendoReviews();
    this.initVariantPriceListener();
  }

  private removeGrouptifyLinks(): void {
    // Wait up to 10 seconds for at least one matching element to appear, then remove as before
    const removeGrouptifyLinks = () => {
      document
        .querySelectorAll("a[href*=grouptify-combined-listings]")
        .forEach((el) => {
          const parent = el.parentElement;
          if (parent && parent.tagName === "DIV") {
            parent.remove();
          } else {
            el.remove();
          }
        });
      document
        .querySelectorAll('[title="Powered by Grouptify"]')
        .forEach((el) => el.remove());
    };

    const waitForGrouptifyLinks = (timeout = 10000) => {
      const start = Date.now();
      const check = () => {
        if (
          document.querySelector("a[href*=grouptify-combined-listings]") ||
          document.querySelector('[title="Powered by Grouptify"]')
        ) {
          removeGrouptifyLinks();
        } else if (Date.now() - start < timeout) {
          setTimeout(check, 100);
        }
      };
      check();
    };

    waitForGrouptifyLinks();
  }

  private initMobileSwatchSlider(): void {
    // Wait for MPG swatch navigation elements to appear
    const waitForSwatchNav = (timeout = 10000) => {
      const start = Date.now();
      const check = () => {
        const swatchNavs = document.querySelectorAll(
          ".mpg-swatch-image-button.mpg-swatch__nav"
        );

        if (swatchNavs.length > 0) {
          this.wrapSwatchNavs(swatchNavs);
        } else if (Date.now() - start < timeout) {
          setTimeout(check, 100);
        }
      };
      check();
    };

    waitForSwatchNav();
  }

  private wrapSwatchNavs(swatchNavs: NodeListOf<Element>): void {
    swatchNavs.forEach((navElement) => {
      // Check if already wrapped
      if (
        navElement.parentElement?.classList.contains("mpg-swatch-nav-wrapper")
      ) {
        return;
      }

      // Create wrapper
      const wrapper = document.createElement("div");
      wrapper.className = "mpg-swatch-nav-wrapper";

      // Insert wrapper before the nav element
      navElement.parentNode?.insertBefore(wrapper, navElement);

      // Move nav element into wrapper
      wrapper.appendChild(navElement);

      console.log("MPG swatch nav wrapped for mobile slider");
    });
  }

  private initBuyButtons(): void {
    let productForms = document.querySelector(".product-form");
    this.productFormParent = productForms?.parentElement as HTMLElement | null;

    if (this.productFormParent) {
      this.productFormParent.classList.add("product-form-wrapper");
    }

    this.initRechargeWidget();
    this.moveQuantityToButtons();
    this.animateProductInfoWrapper();
  }

  private initVariantPriceListener(): void {
    // Wait for the variant input to be available
    const waitForVariantInput = (retries = 20) => {
      const variantInput = document.querySelector(
        'input[name="id"].product-variant-id'
      ) as HTMLInputElement;

      if (variantInput) {
        // Listen to change event on the variant input
        variantInput.addEventListener(
          "change",
          this.handleVariantChange.bind(this)
        );
        // Also trigger on initial load to set initial price
        this.handleVariantChange();
      } else if (retries > 0) {
        setTimeout(() => waitForVariantInput(retries - 1), 100);
      }
    };
    waitForVariantInput();
  }

  /** Badges SAVE: one-time. .nad-pack-chip__per: precio por lata según selling plan seleccionado. */
  private static enrichPackSizeChipsFromPackJson(): void {
    const data = readNadPackDataJson();
    if (!data?.variants?.length) return;

    const sellingPlanId = getSelectedSellingPlanIdFromDom();

    const hasAnyPrice = data.variants.some(
      (v) =>
        v.price > 0 ||
        (v.sellingPlanAllocations?.some((a) => a.price > 0) ?? false)
    );
    if (!hasAnyPrice) return;

    const resolved = data.variants
      .map((v) => {
        const fromTitle = v.title ? parsePackSizeFromLabel(v.title) : null;
        const pack =
          v.packSize > 0
            ? v.packSize
            : parsePackSizeFromLabel(v.packLabel) || fromTitle || 0;
        return { row: v, pack };
      })
      .filter((x) => x.row.price > 0);

    const withPack = resolved.filter((x) => x.pack > 0);
    const sorted = [...withPack].sort((a, b) => a.pack - b.pack);
    const tierPacks = [...new Set(sorted.map((s) => s.pack))].sort(
      (a, b) => a - b
    );
    const baseline = sorted[0];
    const baselineEffPrice = baseline ? baseline.row.price : 0;
    const baselinePerCanCents = baseline
      ? Math.round(baselineEffPrice / baseline.pack)
      : 0;

    const container = findPackSizeOptionSetContainer();
    if (!container) return;

    const options = queryPackSizeChipElements(container);
    if (!options.length) return;

    const byVariantId = new Map<string, NadPackVariantJson>();
    for (const v of data.variants) {
      if (v.price > 0) byVariantId.set(String(v.id), v);
    }

    const byPack = new Map<number, NadPackVariantJson>();
    for (const { row, pack } of withPack) {
      if (!byPack.has(pack)) byPack.set(pack, row);
    }

    options.forEach((el) => {
      el
        .querySelectorAll(
          ".nad-pack-chip__icons, .nad-pack-chip__per-wrap, .nad-pack-chip__per, .nad-pack-chip__save"
        )
        .forEach((n) => n.remove());

      const chipText = stripPackChipEnrichmentText(el);

      let row: NadPackVariantJson | undefined;
      const vid = getVariantIdFromPackChipEl(el);
      if (vid) row = byVariantId.get(vid);

      if (!row) {
        const pack = parsePackSizeFromLabel(chipText);
        if (pack != null) row = byPack.get(pack);
      }
      if (!row) return;

      const effPack = packSizeForVariantRow(row, chipText);

      let tierSaveCents = 0;
      let showTierSaveBadge = false;
      if (
        baseline &&
        baselinePerCanCents > 0 &&
        effPack > 0 &&
        row.id !== baseline.row.id
      ) {
        const rowEffTier = row.price;
        const hypotheticalCents = baselinePerCanCents * effPack;
        tierSaveCents = hypotheticalCents - rowEffTier;
        showTierSaveBadge = tierSaveCents >= 100;
      }

      if (effPack > 0) {
        const iconCount = tierPacks.length
          ? Math.max(1, tierPacks.filter((p) => p <= effPack).length)
          : 1;
        const iconsWrap = document.createElement("div");
        iconsWrap.className = "nad-pack-chip__icons";
        for (let i = 0; i < iconCount; i++) {
          iconsWrap.appendChild(createPackCubeIcon());
        }
        el.insertBefore(iconsWrap, el.firstChild);
      }

      const perPair = perCanForChipDisplay(row, chipText, sellingPlanId);
      if (!perPair) return;

      const wrap = document.createElement("span");
      wrap.className = "nad-pack-chip__per-wrap";

      const saveLine = packLineSaveCents(row, chipText);
      if (saveLine != null && saveLine > 0 && !showTierSaveBadge) {
        const lineBadge = document.createElement("span");
        lineBadge.className = "nad-pack-chip__per-compare";
        lineBadge.textContent = `SAVE ${formatSaveBadgeAmount(saveLine, data.currency)}`;
        wrap.appendChild(lineBadge);
      }

      const perEl = document.createElement("span");
      perEl.className = "nad-pack-chip__per";
      perEl.textContent = formatPerCanLabel(perPair.salePerCan, data.currency);
      wrap.appendChild(perEl);
      el.appendChild(wrap);

      if (!baseline || baselinePerCanCents <= 0) return;

      if (effPack <= 0) return;

      if (showTierSaveBadge) {
        const badge = document.createElement("span");
        badge.className = "nad-pack-chip__save";
        badge.textContent = `SAVE $${Math.round(tierSaveCents / 100)}`;
        el.appendChild(badge);
      }
    });
  }

  private static packJsonEnrichmentBooted = false;

  /** Una sola inicialización global del enrich de packs: PDP, featured product, etc. */
  private static bootPackSizeChipsFromPackJson(): void {
    if (NADProductTemplate.packJsonEnrichmentBooted) return;
    NADProductTemplate.packJsonEnrichmentBooted = true;

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const schedule = (): void => {
      if (debounceTimer != null) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        NADProductTemplate.enrichPackSizeChipsFromPackJson();
      }, 120);
    };

    const run = () => NADProductTemplate.enrichPackSizeChipsFromPackJson();

    run();

    document.addEventListener("product-info:loaded", run);

    const onSellingPlanField = (e: Event): void => {
      const t = e.target as HTMLInputElement | HTMLSelectElement | null;
      if (t && t.name === "selling_plan") schedule();
    };
    document.addEventListener("change", onSellingPlanField, true);
    document.addEventListener("input", onSellingPlanField, true);

    const observe = (root: Element): void => {
      const obs = new MutationObserver((records) => {
        if (shouldSchedulePackChipEnrichmentFromMutations(records)) schedule();
      });
      obs.observe(root, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ["value", "checked"],
      });
    };

    const pi = document.querySelector("product-info");
    if (pi) observe(pi);

    let rechargeObsAttempts = 0;
    const tryObserveRecharge = (): void => {
      const w = document.querySelector("recharge-subscription-widget");
      if (w) {
        observe(w);
        return;
      }
      if (rechargeObsAttempts++ < 40) setTimeout(tryObserveRecharge, 250);
    };
    tryObserveRecharge();

    let attempts = 0;
    let pollDone = false;
    const poll = () => {
      if (pollDone || attempts++ > 100) return;
      const packContainer = findPackSizeOptionSetContainer();
      const hasPack =
        packContainer && queryPackSizeChipElements(packContainer).length > 0;
      if (hasPack) {
        run();
        pollDone = true;
        return;
      }
      setTimeout(poll, 100);
    };
    setTimeout(poll, 0);
  }

  private handleVariantChange(): void {
    try {
      // Small delay to ensure variant-selects has updated
      setTimeout(() => {
        // Get the variant ID from the input
        const variantInput = document.querySelector(
          'input[name="id"].product-variant-id'
        ) as HTMLInputElement;

        if (!variantInput || !variantInput.value) {
          return;
        }

        // Get variant data from variant-selects element
        const variantSelects = document.querySelector("variant-selects");
        if (!variantSelects) {
          return;
        }

        const variantDataScript = variantSelects.querySelector(
          '[data-selected-variant]'
        ) as HTMLElement;

        if (!variantDataScript) {
          return;
        }

        let variant: any = null;
        try {
          variant = JSON.parse(variantDataScript.textContent || "{}");
        } catch (error) {
          console.warn("Error parsing variant data:", error);
          return;
        }

        if (!variant || !variant.price) {
          return;
        }

        // Update price in all submit buttons (original and wrapped)
        const submitButtons = document.querySelectorAll(
          ".product-form__submit, .product-form__submit--wrapped"
        );

        submitButtons.forEach((button) => {
          const priceWrapper = button.querySelector(
            "[data-product-submit-price-wrapper]"
          ) as HTMLElement;
          const priceElement = button.querySelector(
            "[data-product-submit-price]"
          ) as HTMLElement;
          const comparePriceElement = button.querySelector(
            "[data-product-submit-price-compare]"
          ) as HTMLElement;

          if (!priceElement) return;

          // Hide price if variant is not available
          if (!variant.available || !variant.price) {
            if (priceWrapper) priceWrapper.style.display = "none";
            return;
          }

          // Show price wrapper
          if (priceWrapper) priceWrapper.style.display = "";

          // Check if variant is on sale
          const isOnSale =
            variant.compare_at_price &&
            variant.compare_at_price > variant.price;

          // Try to get formatted prices from the main price element on page first
          // This is more reliable as Shopify updates it automatically
          const priceContainer = document.querySelector("[id^='price-']");

          if (priceContainer) {
            if (isOnSale) {
              // When on sale, look inside .price__sale container
              const priceSaleContainer = priceContainer.querySelector(
                ".price__sale"
              );
              
              if (priceSaleContainer) {
                // Get sale price (current price) - this is the price-item--sale
                const salePriceElement = priceSaleContainer.querySelector(
                  ".price-item--sale, .price-item--last"
                ) as HTMLElement;
                
                // Get compare at price (original price strikethrough)
                // The structure is: <s class="price-item price-item--regular">content</s>
                const compareAtPriceElement = priceSaleContainer.querySelector(
                  "s.price-item--regular"
                ) as HTMLElement;

                if (salePriceElement && salePriceElement.textContent) {
                  priceElement.textContent = salePriceElement.textContent.trim();
                }

                if (compareAtPriceElement && compareAtPriceElement.textContent) {
                  const comparePriceText = compareAtPriceElement.textContent.trim();
                  
                  if (comparePriceElement) {
                    comparePriceElement.textContent = comparePriceText;
                    comparePriceElement.style.display = "";
                  } else if (priceWrapper) {
                    // Create compare price element if it doesn't exist
                    const compareEl = document.createElement("s");
                    compareEl.className = "product-form__submit-price-compare";
                    compareEl.setAttribute(
                      "data-product-submit-price-compare",
                      ""
                    );
                    compareEl.textContent = comparePriceText;
                    priceWrapper.insertBefore(compareEl, priceElement);
                  }
                } else if (comparePriceElement) {
                  comparePriceElement.style.display = "none";
                }
              }
            } else {
              // When not on sale, look inside .price__regular container
              const priceRegularContainer = priceContainer.querySelector(
                ".price__regular"
              );
              
              if (priceRegularContainer) {
                const regularPriceElement = priceRegularContainer.querySelector(
                  ".price-item--regular"
                ) as HTMLElement;

                if (regularPriceElement && regularPriceElement.textContent) {
                  priceElement.textContent = regularPriceElement.textContent.trim();
                }
              }
              
              // Hide compare price when not on sale
              if (comparePriceElement) {
                comparePriceElement.style.display = "none";
              }
            }
            return;
          }

          // Fallback: format prices using Shopify.formatMoney
          if (
            (window as any).Shopify &&
            typeof (window as any).Shopify.formatMoney === "function"
          ) {
            try {
              // Get money format - try different possible locations
              const moneyFormat =
                (window as any).Shopify.money_format ||
                (window as any).Shopify.moneyFormat ||
                (window as any).theme?.moneyFormat ||
                "{{amount}}";

              // Format sale price (current price)
              const formattedPrice = (window as any).Shopify.formatMoney(
                variant.price,
                moneyFormat
              );
              priceElement.textContent = formattedPrice;

              // Format compare at price if on sale
              if (isOnSale && variant.compare_at_price) {
                const formattedComparePrice = (window as any).Shopify.formatMoney(
                  variant.compare_at_price,
                  moneyFormat
                );

                if (comparePriceElement) {
                  comparePriceElement.textContent = formattedComparePrice;
                  comparePriceElement.style.display = "";
                } else if (priceWrapper) {
                  // Create compare price element if it doesn't exist
                  const compareEl = document.createElement("s");
                  compareEl.className = "product-form__submit-price-compare";
                  compareEl.setAttribute(
                    "data-product-submit-price-compare",
                    ""
                  );
                  compareEl.textContent = formattedComparePrice;
                  priceWrapper.insertBefore(compareEl, priceElement);
                }
              } else if (comparePriceElement) {
                comparePriceElement.style.display = "none";
              }
            } catch (error) {
              console.warn("Error formatting price:", error);
              if (priceWrapper) priceWrapper.style.display = "none";
            }
          } else {
            // Last fallback: show price as-is
            priceElement.textContent = variant.price.toString();
            if (comparePriceElement && isOnSale && variant.compare_at_price) {
              comparePriceElement.textContent =
                variant.compare_at_price.toString();
              comparePriceElement.style.display = "";
            } else if (comparePriceElement) {
              comparePriceElement.style.display = "none";
            }
          }
        });
      }, 50);
    } catch (error) {
      console.warn("Error handling variant change:", error);
    }
  }

  private initRechargeWidget(): void {
    const productFormParent = this.productFormParent;
    const cssUrl = window.rechargeWidgetCssUrl ?? "";

    let debounceInject: ReturnType<typeof setTimeout> | null = null;
    const scheduleInject = (widget: Element) => {
      if (!cssUrl) return;
      if (debounceInject != null) clearTimeout(debounceInject);
      debounceInject = setTimeout(() => {
        debounceInject = null;
        injectRechargeMinCssIntoShadowRoots(widget, cssUrl);
      }, 50);
    };

    const waitForRechargeWidget = (retries = 40) => {
      this.rechargeWidget = document.querySelector(
        "recharge-subscription-widget"
      );
      if (
        this.rechargeWidget &&
        productFormParent &&
        this.rechargeWidget.parentElement !== productFormParent
      ) {
        productFormParent.prepend(this.rechargeWidget);
      }

      if (this.rechargeWidget) {
        if (cssUrl) {
          injectRechargeMinCssIntoShadowRoots(this.rechargeWidget, cssUrl);
          for (const ms of [100, 350, 700]) {
            setTimeout(() => {
              if (this.rechargeWidget) {
                injectRechargeMinCssIntoShadowRoots(this.rechargeWidget, cssUrl);
              }
            }, ms);
          }
        }
        if (!this.rechargeShadowObserver) {
          this.rechargeShadowObserver = new MutationObserver(() => {
            scheduleInject(this.rechargeWidget!);
          });
          this.rechargeShadowObserver.observe(this.rechargeWidget, {
            subtree: true,
            childList: true,
          });
        }
      } else if (retries > 0) {
        setTimeout(() => waitForRechargeWidget(retries - 1), 150);
      }
    };
    waitForRechargeWidget();
  }

  private moveQuantityToButtons(): void {
    // Find the quantity input container and submit button
    const quantityContainer = document.querySelector(".product-form__quantity");
    const submitButton = document.querySelector(".product-form__submit");
    const buttonsContainer = document.querySelector(".product-form__buttons");

    if (quantityContainer && submitButton && buttonsContainer) {
      // Create wrapper for quantity + submit
      const wrapper = document.createElement("div");
      wrapper.classList.add("product-form__quantity-submit-wrapper");

      // Clone the quantity container
      const quantityClone = quantityContainer.cloneNode(true) as HTMLElement;
      quantityClone.classList.add("product-form__quantity--prepend");

      // Clone the submit button
      const submitClone = submitButton.cloneNode(true) as HTMLElement;
      submitClone.classList.add("product-form__submit--wrapped");

      // Add elements to wrapper
      wrapper.appendChild(quantityClone);
      wrapper.appendChild(submitClone);

      // Remove originals
      quantityContainer.remove();
      submitButton.remove();

      // Add wrapper to buttons container
      buttonsContainer.prepend(wrapper);

      console.log(
        "Quantity input and submit button wrapped and moved to buttons container"
      );

      // Append 'ready' class to the wrapper after it is added to the DOM
      wrapper.classList.add("product-form__quantity-submit-wrapper--ready");
    } else {
      console.warn("Could not find required elements", {
        quantityContainer: !!quantityContainer,
        submitButton: !!submitButton,
        buttonsContainer: !!buttonsContainer,
      });
    }
  }

  private animateProductInfoWrapper(): void {
    // Animate product info wrapper fade-in
    const productInfoWrappers = document.querySelectorAll(
      ".product__info-wrapper"
    ) as NodeListOf<HTMLElement>;

    productInfoWrappers.forEach((wrapper, index) => {
      if (wrapper) {
        // Add fade-in class to trigger animation
        wrapper.classList.add("product__info-wrapper--animate-in");
        console.log(
          `Product info wrapper ${index + 1} fade in animation triggered`
        );
      }
    });

    if (productInfoWrappers.length === 0) {
      console.warn("Could not find any product info wrappers for animation");
    }
  }

  private initOkendoReviews(): void {
    const okendoReviews = document.querySelector(
      "[id*=__okendo_reviews_widget_]"
    );
    if (okendoReviews) {
      // Prepend heading inside the reviews element
      const heading = document.createElement("div");
      heading.className = "reviews-heading";
      heading.innerHTML = `<span>Reviews</span><h2>Your stories, our inspiration</h2>`;
      okendoReviews.insertBefore(heading, okendoReviews.firstChild);
    }

    // Do the same for the Okendo reviews carousel element
    const okendoReviewsCarousel = document.querySelector(
      '[id*="__okendo_reviews_carousel_"]'
    );
    if (okendoReviewsCarousel) {
      const heading = document.createElement("div");
      heading.className = "reviews-heading";
      heading.innerHTML = `<span>Backed by over a century of research</span><h2>Not a buzzword.<br>It’s proven science.</h2>`;
      okendoReviewsCarousel.insertBefore(
        heading,
        okendoReviewsCarousel.firstChild
      );
    }
  }

  // Static method for automatic initialization following theme pattern
  static initializeAll(): void {
    NADProductTemplate.bootPackSizeChipsFromPackJson();
    const elements =
      document.querySelectorAll<HTMLElement>(".template-product");
    elements.forEach((element) => {
      if (!(element as any)._nadProductFormInstance) {
        (element as any)._nadProductFormInstance = new NADProductTemplate();
      }
    });
  }

  // Static method for cleanup
  static destroyAll(): void {
    const elements =
      document.querySelectorAll<HTMLElement>(".template-product");
    elements.forEach((element) => {
      const instance = (element as any)._nadProductFormInstance;
      if (instance) {
        delete (element as any)._nadProductFormInstance;
      }
    });
  }
}

// Export for use in other modules
export { NADProductTemplate };
